import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import ButtonLoader from "../../components/common/ButtonLoader";
import axiosInstance from "../../utils/axios";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({ email: "", newPassword: "" });
  const [successMessage, setSuccessMessage] = useState("");

  const validateForm = () => {
    let valid = true;
    const newErrors = { email: "", newPassword: "" };

    if (!email) {
      newErrors.email = "Email is required.";
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Invalid email format.";
      valid = false;
    }

    if (!newPassword) {
      newErrors.newPassword = "New password is required.";
      valid = false;
    } else if (newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleForgotPassword = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setSuccessMessage("");
    try {
      const { data } = await axiosInstance.post("admins/admin-forgot-password", {
        email,
        newPassword,
      });

      if (data?.message) {
        setSuccessMessage(data.message);
        setEmail("");
        setNewPassword("");
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      if (error.response?.status === 404) {
        setErrors((prev) => ({
          ...prev,
          email: "No account found with this email.",
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          email: "Something went wrong. Please try again.",
        }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-tr from-slate-300 to-slate-100 flex items-center justify-center px-4">
      <div className="w-full max-w-lg bg-white rounded-xl shadow-xl p-10">
        <h1 className="text-center text-2xl font-bold text-primaryColor mb-1">
          Reset Your Password
        </h1>
        <p className="text-center text-sm text-gray-500 mb-6">
          Enter your email and new password to reset your account
        </p>

        <div className="space-y-5">
          {/* Email Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`mt-1 block w-full px-4 py-2 border ${errors.email ? "border-red-500" : "border-gray-300"
                } rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primaryColor`}
              placeholder="Enter your email"
            />
            {errors.email && (
              <p className="text-xs text-red-500 mt-1">{errors.email}</p>
            )}
          </div>

          {/* New Password Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={`mt-1 block w-full px-4 py-2 border ${errors.newPassword ? "border-red-500" : "border-gray-300"
                  } rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primaryColor pr-10`}
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-primaryColor"
              >
                {!showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {errors.newPassword && (
              <p className="text-xs text-red-500 mt-1">{errors.newPassword}</p>
            )}
          </div>

          {/* Success Message */}
          {successMessage && (
            <p className="text-sm text-green-600 text-center">
              {successMessage}
            </p>
          )}

          {/* Submit Button */}
          <div>
            <button
              onClick={handleForgotPassword}
              disabled={isLoading}
              className="w-full h-12 flex justify-center items-center bg-primaryBtnColor text-white font-semibold rounded-md transition hover:bg-primaryBtnHoverColor"
            >
              {isLoading ? <ButtonLoader /> : "Reset Password"}
            </button>
          </div>

          {/* Back to Login */}
          <div className="text-center mt-4">
            <button
              onClick={() => navigate("/login")}
              className="text-sm text-secondaryTextColor hover:text-primaryColor"
            >
              ← Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
